import { useEffect, useState } from 'react'
import { Alert, Button, Checkbox, Input, InputNumber, Modal, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography, message } from 'antd'
import { DeleteOutlined, EditOutlined, LockOutlined, PlusOutlined } from '@ant-design/icons'
import { rolesApi, type PermissionDef, type RoleInfo } from '../services/rolesApi'
import { useRolesStore } from '../store/rolesStore'
import { useAuthStore } from '../store/authStore'
import { DEV_ADMIN } from '../constants/roles'

const { Text } = Typography

function extractError(error: unknown): string {
    const detail = (error as { response?: { data?: { detail?: unknown } } })?.response?.data?.detail
    if (typeof detail === 'string') return detail
    return 'Не удалось выполнить действие'
}

// Роли и их права. Любая правка применяется сразу: сервер перечитывает права
// после записи, и следующий же запрос проверяется по новым правилам. Каждое
// изменение попадает в «Журнал действий».
export default function RolesSettings() {
    const catalog = useRolesStore((state) => state.catalog)
    const load = useRolesStore((state) => state.load)
    const { user } = useAuthStore()
    const [savingKey, setSavingKey] = useState<string | null>(null)

    const [createOpen, setCreateOpen] = useState(false)
    const [newLabel, setNewLabel] = useState('')
    const [copyFrom, setCopyFrom] = useState<string | undefined>(undefined)

    const [renaming, setRenaming] = useState<RoleInfo | null>(null)
    const [renameValue, setRenameValue] = useState('')

    // Открыли раздел - берём свежий справочник: его могли поменять в другой вкладке.
    useEffect(() => {
        load().catch((error) => message.error(extractError(error)))
    }, [load])

    const roles = catalog?.roles ?? []
    const isDev = user?.role === DEV_ADMIN
    const myPermissions = new Set(user?.permissions ?? [])

    // Те же ограничения, что проверяет сервер: СуперАдминистратор не правит
    // свою роль и не выдаёт права, которых у него самого нет.
    const isOwnRole = (role: RoleInfo) => !isDev && role.code === user?.role
    const lacksPermission = (code: string) => !isDev && !myPermissions.has(code)

    const run = async (key: string, action: () => Promise<unknown>, success?: string) => {
        setSavingKey(key)
        try {
            await action()
            await load()
            if (success) message.success(success)
        } catch (error) {
            message.error(extractError(error))
        } finally {
            setSavingKey(null)
        }
    }

    const togglePermission = (role: RoleInfo, permission: string, checked: boolean) => {
        const next = checked
            ? [...role.permissions, permission]
            : role.permissions.filter((code) => code !== permission)
        run(`${role.code}:${permission}`, () => rolesApi.update(role.code, { permissions: next }))
    }

    const submitCreate = async () => {
        const label = newLabel.trim()
        if (!label) {
            message.warning('Введите название роли')
            return
        }
        await run('create', () => rolesApi.create(label, copyFrom), `Роль «${label}» заведена`)
        setCreateOpen(false)
        setNewLabel('')
        setCopyFrom(undefined)
    }

    const submitRename = async () => {
        if (!renaming) return
        const label = renameValue.trim()
        if (!label) return
        await run(`rename:${renaming.code}`, () => rolesApi.update(renaming.code, { label }), 'Роль переименована')
        setRenaming(null)
    }

    const deleteBlockedReason = (role: RoleInfo) => {
        if (role.user_count > 0) return `У роли ${role.user_count} сотрудник(ов) - сначала смените им роль`
        return null
    }

    const roleColumns = [
        {
            title: 'Роль',
            key: 'label',
            render: (_: unknown, role: RoleInfo) => (
                <Space size={8} wrap>
                    <Text strong>{role.label}</Text>
                    {role.locked && <Tag icon={<LockOutlined />} color="purple">всё доступно</Tag>}
                    {isOwnRole(role) && <Tag color="gold">ваша роль</Tag>}
                </Space>
            ),
        },
        {
            title: (
                <Tooltip title="Старшинство роли: чем больше, тем старше. Задачи руководитель видит и ставит только тем, кто младше по рангу.">
                    <span>Ранг</span>
                </Tooltip>
            ),
            key: 'rank',
            width: 110,
            render: (_: unknown, role: RoleInfo) => role.locked
                ? <Text type="secondary">старше всех</Text>
                : (
                    <InputNumber
                        min={1}
                        max={999}
                        size="small"
                        defaultValue={role.sort_order}
                        key={`${role.code}:${role.sort_order}`}
                        disabled={savingKey !== null}
                        style={{ width: 80 }}
                        onBlur={(event) => {
                            const value = Number(event.target.value)
                            if (!value || value === role.sort_order) return
                            run(`rank:${role.code}`, () => rolesApi.update(role.code, { sort_order: value }), 'Ранг сохранён')
                        }}
                    />
                ),
        },
        {
            title: 'Код',
            dataIndex: 'code',
            width: 150,
            render: (value: string) => <Text type="secondary" code>{value}</Text>,
        },
        {
            title: 'Сотрудников',
            dataIndex: 'user_count',
            width: 130,
            render: (value: number) => <Tag color={value ? 'blue' : 'default'}>{value}</Tag>,
        },
        {
            title: '',
            key: 'actions',
            width: 110,
            render: (_: unknown, role: RoleInfo) => {
                if (role.locked) return null
                const blocked = deleteBlockedReason(role)
                return (
                    <Space size={4}>
                        <Tooltip title="Переименовать">
                            <Button
                                type="text"
                                icon={<EditOutlined />}
                                onClick={() => { setRenaming(role); setRenameValue(role.label) }}
                            />
                        </Tooltip>
                        {blocked ? (
                            <Tooltip title={blocked}>
                                <Button type="text" danger icon={<DeleteOutlined />} disabled />
                            </Tooltip>
                        ) : (
                            <Popconfirm
                                title={`Удалить роль «${role.label}»?`}
                                description="Роль не вернётся сама - её придётся заводить заново. Действие попадёт в журнал."
                                okText="Удалить"
                                cancelText="Отмена"
                                onConfirm={() => run(`delete:${role.code}`, () => rolesApi.remove(role.code), 'Роль удалена')}
                            >
                                <Tooltip title="Удалить">
                                    <Button type="text" danger icon={<DeleteOutlined />} />
                                </Tooltip>
                            </Popconfirm>
                        )}
                    </Space>
                )
            },
        },
    ]

    // Таблица прав: строки - права, столбцы - роли. Ширина по содержимому, чтобы
    // все роли помещались без прокрутки; прокрутка появится, только если ролей
    // станет больше, чем влезает в экран.
    const permissionColumns = [
        {
            title: 'Право',
            key: 'permission',
            fixed: 'left' as const,
            width: 280,
            render: (_: unknown, permission: PermissionDef) => (
                <Space direction="vertical" size={0}>
                    <Text>{permission.label}</Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>{permission.description}</Text>
                </Space>
            ),
        },
        ...roles.map((role) => ({
            title: role.label,
            key: role.code,
            width: 120,
            align: 'center' as const,
            render: (_: unknown, permission: PermissionDef) => {
                if (role.locked) {
                    return (
                        <Tooltip title="«Админ разработки» не ограничивается">
                            <Checkbox checked disabled />
                        </Tooltip>
                    )
                }
                const checked = role.permissions.includes(permission.code)
                let reason: string | null = null
                if (isOwnRole(role)) reason = 'Права своей роли меняет «Админ разработки»'
                else if (!checked && lacksPermission(permission.code)) reason = 'Нельзя выдать право, которого нет у вас'
                const box = (
                    <Checkbox
                        checked={checked}
                        disabled={savingKey !== null || reason !== null}
                        onChange={(event) => togglePermission(role, permission.code, event.target.checked)}
                    />
                )
                return reason ? <Tooltip title={reason}>{box}</Tooltip> : box
            },
        })),
    ]

    return (
        <Space direction="vertical" size={20} style={{ width: '100%' }}>
            <Alert
                type="info"
                showIcon
                message="Правки применяются сразу"
                description={
                    'Галочка сохраняется в момент нажатия, и уже следующий запрос сотрудника проверяется '
                    + 'по новым правам - без перезапуска. Каждое изменение пишется в «Журнал действий». '
                    + 'Роль «Админ разработки» не редактируется: ей всегда доступно всё. '
                    + 'Удалить можно любую другую роль, если на ней нет сотрудников. '
                    + 'Ранг задаёт старшинство: задачи руководитель видит и ставит только тем, кто младше.'
                }
            />

            <Space style={{ width: '100%', justifyContent: 'space-between' }} wrap>
                <Text strong>Роли</Text>
                <Button type="primary" icon={<PlusOutlined />} onClick={() => setCreateOpen(true)}>
                    Создать роль
                </Button>
            </Space>

            <Table
                rowKey="code"
                size="small"
                columns={roleColumns}
                dataSource={roles}
                pagination={false}
                loading={!catalog}
            />

            <Text strong>Права по ролям</Text>
            <Table
                rowKey="code"
                size="small"
                columns={permissionColumns}
                dataSource={catalog?.permissions ?? []}
                pagination={false}
                loading={!catalog}
                scroll={{ x: 'max-content' }}
            />

            <Modal
                title="Новая роль"
                open={createOpen}
                onCancel={() => setCreateOpen(false)}
                onOk={submitCreate}
                okText="Создать"
                cancelText="Отмена"
                confirmLoading={savingKey === 'create'}
            >
                <Space direction="vertical" size={12} style={{ width: '100%' }}>
                    <Input
                        placeholder="Название роли, например «АМ-старший»"
                        maxLength={100}
                        value={newLabel}
                        onChange={(event) => setNewLabel(event.target.value)}
                    />
                    <Select
                        allowClear
                        placeholder="Скопировать права и статусы с роли (необязательно)"
                        style={{ width: '100%' }}
                        value={copyFrom}
                        onChange={setCopyFrom}
                        options={roles
                            .filter((role) => !role.locked)
                            .map((role) => ({ value: role.code, label: role.label }))}
                    />
                    <Text type="secondary" style={{ fontSize: 12 }}>
                        Без копирования роль заводится пустой - без прав и без статусов. Права и
                        статусы потом отмечаются галочками в таблицах.
                    </Text>
                </Space>
            </Modal>

            <Modal
                title={renaming ? `Переименовать «${renaming.label}»` : ''}
                open={Boolean(renaming)}
                onCancel={() => setRenaming(null)}
                onOk={submitRename}
                okText="Сохранить"
                cancelText="Отмена"
                confirmLoading={savingKey?.startsWith('rename:')}
            >
                <Input
                    maxLength={100}
                    value={renameValue}
                    onChange={(event) => setRenameValue(event.target.value)}
                />
            </Modal>
        </Space>
    )
}
